import React, { type FormEventHandler, useState } from 'react';
import { Box, Button, Icon, InlineMessage, P } from '@saviynt/design-system';
import cx from 'classnames';
import { OTPInput, REGEXP_ONLY_DIGITS, type RenderProps } from 'input-otp';

import { FormLayout } from '../../../shared/components';
import { validateTotpCodeAtLogin } from '../../../shared/data-sources';

import './MfaVerificationForm.css';

interface MfaVerificationProps {
  onCancel: () => void;
  onSuccess: () => void;
}

/** Allows entry of MFA code during login or other step-up auth flow (first time
 * activating MFA high-risk action but they never setup MFA previously) */
export default function MfaVerificationForm({
  onCancel: handleCancel,
  onSuccess,
}: MfaVerificationProps): JSX.Element | null {
  const [helpMode, setHelpMode] = useState<string | null | false>(null);
  const [code, setCode] = useState('');

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    handleComplete();
  };

  const handleComplete = () => {
    if (typeof helpMode === 'string') {
      setHelpMode(false);
    }

    const unexpectedError = 'An unexpected error occurred. Please try again.';

    validateTotpCodeAtLogin(code)
      .then((isValid) => {
        if (isValid) {
          onSuccess();
        } else {
          setHelpMode(
            'Login failed. Please try again. Too many attempts might lock you out.'
          );
        }
      })
      .catch(() => {
        setHelpMode(unexpectedError);
      });
  };

  return (
    <FormLayout
      title='Verify your identity'
      footerContent={footer()}
      onSubmit={handleSubmit}>
      <P className='Auth_MfaVerification-description'>
        Open your authenticator and enter the passcode to verify your identity.
      </P>
      <OTPInput
        maxLength={6}
        value={code}
        onChange={setCode}
        inputMode='numeric'
        containerClassName='Auth_MfaVerification-input'
        pattern={REGEXP_ONLY_DIGITS}
        onComplete={handleComplete}
        render={render}
      />
      <Box className='Auth_MfaVerification-help'>{helpContent()}</Box>
    </FormLayout>
  );

  function helpContent() {
    switch (helpMode) {
      case null: {
        return <P variant={3}>Contact your admin if you need help</P>;
      }

      case false: {
        // hide help text while preventing layout shift
        return <P variant={3}>&nbsp;</P>;
      }

      default: {
        return (
          <InlineMessage
            text={helpMode}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        );
      }
    }
  }

  function render({ slots }: RenderProps) {
    return slots.map((slot, index) => {
      const className = cx({
        'Auth_MfaVerification-input-codeBox': true,
        'Auth_MfaVerification-input-codeBox--isActive': slot.isActive,
      });

      return (
        <Box
          // eslint-disable-next-line react/no-array-index-key -- based on input-otp docs
          key={index}
          tabIndex={-1}
          className={className}>
          {slot.char}
        </Box>
      );
    });
  }

  function footer() {
    return (
      <Box className='Auth_MfaVerificationForm-footer'>
        <Button onClick={handleCancel} type='button' kind='ghost'>
          Back to Login
        </Button>
      </Box>
    );
  }
}
